import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

const CountUp = ({ num, suffix = "", play }) => {
  const reduce = useReducedMotion();
  const [val, setVal] = useState(reduce ? num : 0);

  useEffect(() => {
    if (!play || reduce) {
      setVal(num);
      return;
    }
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(num * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [play, num, reduce]);

  return <>{val.toLocaleString()}{suffix}</>;
};

export const ClinicalSection = () => {
  const { clinical } = useProduct();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  const gridCols = clinical.cols === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3";

  // HP3 "Two decades of dermatology, measured in results." treatment (data flag `clinical.hp3Style`).
  if (clinical.hp3Style) {
    const lines = clinical.titleLines;
    return (
      <section ref={ref} className="bg-riversol-paper py-24 lg:py-32" data-testid="clinical-section" data-clinical-style="hp3">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 max-w-[720px] text-[32px] sm:text-[38px] lg:text-[46px] leading-[1.06] tracking-[-0.02em] text-neutral-900"
          >
            {lines.slice(0, -1).join(" ")}{" "}
            <span className="italic text-riversol-blue">{lines[lines.length - 1]}</span>
          </motion.h2>

          <div className="mt-14 lg:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-y-10 sm:gap-x-10 lg:gap-x-16">
            {clinical.stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.1 }}
                className="relative sm:pl-8 sm:first:pl-0 sm:border-l sm:first:border-l-0 border-neutral-300/70"
                data-testid={`clinical-stat-${i}`}
              >
                <div className="font-mono tabular-nums text-5xl md:text-6xl lg:text-7xl leading-none tracking-tighter text-neutral-900 whitespace-nowrap">
                  {s.num != null ? <CountUp num={s.num} play={inView} /> : s.value}
                  {s.suffix && (
                    <span className="font-mono text-riversol-blue text-2xl md:text-3xl lg:text-4xl align-top ml-1">{s.suffix}</span>
                  )}
                </div>
                {s.title && (
                  <div className="mt-4 font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-900">{s.title}</div>
                )}
                <p className="mt-2 text-[13px] leading-relaxed text-neutral-500 max-w-[240px]">{s.label}</p>
              </motion.div>
            ))}
          </div>

          {clinical.disclaimer && (
            <p className="mt-12 lg:mt-16 text-xs text-neutral-500 font-mono max-w-md leading-relaxed" data-testid="clinical-disclaimer">
              * {clinical.disclaimer}
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="bg-riversol-sand py-20 lg:py-32" data-testid="clinical-section">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="max-w-2xl mb-12 lg:mb-20">
          <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
            {clinical.eyebrow}
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-neutral-900">
            {clinical.titleLines.map((l, i) => <span key={i} className="block">{l}</span>)}
          </h2>
        </div>

        <div className={`grid ${gridCols} gap-px bg-neutral-300 border-y border-neutral-300`}>
          {clinical.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.12} className="bg-riversol-sand">
              <div className="px-6 md:px-8 py-10 md:py-14">
                <div className="font-mono text-5xl md:text-6xl lg:text-7xl text-riversol-blue tracking-tighter mb-4 md:mb-5" data-testid={`clinical-stat-${i}`}>
                  {clinical.animate && s.num != null
                    ? <CountUp num={s.num} suffix={s.suffix} play={inView} />
                    : s.value}
                </div>
                <div className="text-[15px] leading-relaxed text-neutral-600 max-w-[220px]">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-8 lg:mt-10 flex items-start justify-between gap-6 lg:gap-8 flex-wrap">
          <p className="pdp-clinical-disclaimer text-xs text-neutral-500 font-mono max-w-md leading-relaxed">
            * {clinical.disclaimer}
          </p>
          <div className="pdp-clinical-placeholder flex items-center gap-4 border border-dashed border-neutral-400 px-6 py-4">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400">Placeholder</span>
            <span className="text-sm text-neutral-500">Approved before-and-after imagery</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClinicalSection;
